/**
 * The workday move reminder (2026-10-16, cut to one a day 2026-10-17).
 *
 * The camera check only measures; breaks and snacks happen in the user's day.
 * So the reminder lands inside the hours the user said they sit at a desk, at
 * a different time each day — a fixed timer is easy to tune out. One push a
 * day: get up for a few minutes, and on an exercise day do today's snack.
 * More than that starts to feel like nagging; users who want more add their
 * own fixed-time reminders. The team can raise `perDay` later and compare.
 *
 * The advice itself stays the literature's (features/sources, "Rest breaks"):
 * get up every 30–50 min of continuous sitting, for 2–10 min. The program
 * screens say so; the push is the one prompt to act on it.
 *
 * Deterministic: the same user, day and settings always give the same plan, so
 * the scheduler (functions/scheduleReminders) needs no stored state and a
 * re-run never double-sends.
 */
export const NUDGES = {
    /** Pushes per workday. */
    perDay: 1,
    /** The advice: middle of the 30–50 min range (program screens, Today card). */
    breakEveryMinDefault: 45,
    breakEveryMinChoices: [30, 40, 45, 50],
    /** Snacks a day when no program sets the number (the Today card's suggestion). */
    snacksPerDayDefault: 2,
    /** The scheduler runs this often; nudge minutes are multiples of it. */
    tickMin: 10,
    /** Nothing in the first stretch of the day — you just sat down. */
    quietAfterStartMin: 25,
    /** Nothing in the last stretch either. */
    quietBeforeEndMin: 15,
};
/** FNV-1a, enough to turn "uid|2026-10-16" into a stable seed. */
export function hashSeed(text) {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
function rng(seed) {
    let s = seed >>> 0;
    return () => {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/**
 * Today's pushes: the working day split into equal parts, one push at a
 * random time in the middle of each part (never in the quiet stretches at
 * either end). A push is a snack while today has snacks left, otherwise a
 * break. Break reminders off and no snacks: nothing.
 */
export function planDayNudges(input) {
    const { workHours: w } = input;
    if (!w.days.includes(input.dayOfWeek) || w.end <= w.start)
        return [];
    const first = w.start + NUDGES.quietAfterStartMin;
    const last = w.end - NUDGES.quietBeforeEndMin;
    if (last <= first)
        return [];
    const random = rng(input.seed);
    const tick = NUDGES.tickMin;
    const perDay = Math.max(0, input.perDay ?? NUDGES.perDay);
    const count = input.breaks ? perDay : Math.min(perDay, input.snacks);
    const span = last - first;
    const plan = [];
    for (let i = 0; i < count; i++) {
        const lo = first + (i * span) / count;
        const hi = first + ((i + 1) * span) / count;
        const at = lo + (0.2 + 0.6 * random()) * (hi - lo);
        const minute = Math.min(Math.floor(last / tick) * tick, Math.max(Math.ceil(first / tick) * tick, Math.round(at / tick) * tick));
        if (plan.length > 0 && minute <= plan[plan.length - 1].minute)
            continue;
        plan.push(i < input.snacks ? { minute, kind: 'snack', snackIndex: i } : { minute, kind: 'break' });
    }
    return plan;
}
/** Fixed reminders due on this weekday at this tick. */
export function dueFixedReminders(reminders, dayOfWeek, minute) {
    const tick = NUDGES.tickMin;
    return reminders.filter((r) => r.days.includes(dayOfWeek) && r.minute >= minute && r.minute < minute + tick);
}
/** Planned nudges due at this tick (local minute rounded down to the tick). */
export function dueNudges(plan, minute) {
    const tick = NUDGES.tickMin;
    return plan.filter((n) => n.minute >= minute && n.minute < minute + tick);
}
/** "9:30" style label for a minute of the day. */
export function formatMinuteOfDay(minute) {
    const h = Math.floor(minute / 60) % 24;
    const m = minute % 60;
    const suffix = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}
/** Default work hours offered in onboarding: weekdays, 9 to 6. */
export const DEFAULT_WORK_HOURS = { start: 9 * 60, end: 18 * 60, days: [1, 2, 3, 4, 5] };
/** The sitting-hours bucket the coach prompt uses, from work hours. */
export function sittingBucketFromWorkHours(w) {
    const hours = (w.end - w.start) / 60;
    if (hours < 4)
        return '<4';
    if (hours < 6)
        return '4-6';
    if (hours < 8)
        return '6-8';
    return '8+';
}
//# sourceMappingURL=nudges.js.map